import { useMemo } from "react";
import { Video } from "@/machines/types/video";

interface TimelineSegment {
  start: number;
  end: number;
  hasVideo: boolean;
}

/**
 * Hook to determine which timeline segments have video available
 * @param videos - Array of video objects with start_time and end_time
 * @param timelineStart - Start time of the timeline in milliseconds
 * @param timelineEnd - End time of the timeline in milliseconds
 * @returns Array of segments with video availability information
 */
export const useTimelineVideoAvailability = (
  videos: Video[] | undefined,
  timelineStart: Date,
  timelineEnd: Date
) => {
  return useMemo(() => {
    if (!videos || videos.length === 0) {
      return [];
    }

    const startMs = timelineStart.getTime();
    const endMs = timelineEnd.getTime();

    // Create segments for each video
    const videoSegments: TimelineSegment[] = videos.map((video) => ({
      start: new Date(video.start_time).getTime(),
      end: new Date(video.end_time).getTime(),
      hasVideo: true,
    }));

    // Sort segments by start time
    videoSegments.sort((a, b) => a.start - b.start);

    // Merge overlapping segments
    const mergedSegments: TimelineSegment[] = [];
    let currentSegment: TimelineSegment | null = null;

    for (const segment of videoSegments) {
      // Clip segment to timeline bounds
      const clippedStart = Math.max(segment.start, startMs);
      const clippedEnd = Math.min(segment.end, endMs);

      // Skip segments outside timeline
      if (clippedEnd <= startMs || clippedStart >= endMs) continue;

      if (!currentSegment) {
        currentSegment = {
          start: clippedStart,
          end: clippedEnd,
          hasVideo: true,
        };
      } else if (clippedStart <= currentSegment.end) {
        // Overlapping or adjacent - merge
        currentSegment.end = Math.max(currentSegment.end, clippedEnd);
      } else {
        // Gap found - push current and start new
        mergedSegments.push(currentSegment);
        currentSegment = {
          start: clippedStart,
          end: clippedEnd,
          hasVideo: true,
        };
      }
    }

    if (currentSegment) {
      mergedSegments.push(currentSegment);
    }

    return mergedSegments;
  }, [videos, timelineStart, timelineEnd]);
};

/**
 * Check if a specific timestamp has video available
 */
export const checkVideoAvailability = (
  timestamp: number,
  videoSegments: TimelineSegment[]
): boolean => {
  return videoSegments.some(
    (segment) => timestamp >= segment.start && timestamp <= segment.end
  );
};

/**
 * Get video coverage percentage for the timeline
 */
export const getVideoCoveragePercentage = (
  videoSegments: TimelineSegment[],
  timelineStart: number,
  timelineEnd: number
): number => {
  const totalDuration = timelineEnd - timelineStart;
  if (totalDuration === 0) return 0;

  const coveredDuration = videoSegments.reduce(
    (sum, segment) => sum + (segment.end - segment.start),
    0
  );

  return (coveredDuration / totalDuration) * 100;
};
